/** Figma bar thickness for breakdown charts (single + comparison). */
export const CATEGORY_BREAKDOWN_BAR_HEIGHT_PX = 10;

/** @deprecated Use CATEGORY_BREAKDOWN_BAR_HEIGHT_PX */
export const CATEGORY_COMPARISON_BAR_HEIGHT_PX = CATEGORY_BREAKDOWN_BAR_HEIGHT_PX;

/** Single-mode bar thickness — matches production look (thinner bars than comparison mode). */
export const CATEGORY_SINGLE_BAR_HEIGHT_PX = 10;

/**
 * Single-mode row pitch — matches production BarChart, which sized charts at
 * `data.length * 32` so each category band is ~32px tall.
 */
export const CATEGORY_SINGLE_ROW_PITCH_BASE_PX = 32;

export const CATEGORY_COMPARISON_INTRA_GAP_PX = 9;
export const CATEGORY_COMPARISON_INTER_CATEGORY_GAP_PX = 27;

/** Single-mode inter-category gap — matches production look. */
export const CATEGORY_SINGLE_INTER_CATEGORY_GAP_PX = 18;

/** Two bars + gap between them within one category row. */
export const CATEGORY_COMPARISON_GROUP_HEIGHT_PX =
	CATEGORY_BREAKDOWN_BAR_HEIGHT_PX * 2 + CATEGORY_COMPARISON_INTRA_GAP_PX;

/** One category step on the plot (bar group + gap before the next category). */
export const CATEGORY_COMPARISON_ROW_PITCH_PX =
	CATEGORY_COMPARISON_GROUP_HEIGHT_PX + CATEGORY_COMPARISON_INTER_CATEGORY_GAP_PX;

/**
 * Cat-scale paddingInner: inter-category gap / row pitch → 27px gap when step is 61px.
 */
export const CATEGORY_COMPARISON_CATEGORY_PADDING_INNER =
	CATEGORY_COMPARISON_INTER_CATEGORY_GAP_PX / CATEGORY_COMPARISON_ROW_PITCH_PX;

/**
 * G2 dodge: bars are placed at ±(dodgeWidth + margin)/2 from the band center, where
 * dodgeWidth = tickLength * dodgeRatio / 2 and tickLength = BANDWIDTH (not step).
 * bandwidth = step * (1 - paddingInner) = CATEGORY_COMPARISON_GROUP_HEIGHT_PX.
 * visual_gap = (dodgeWidth + margin) - barSize
 *            = dodgeWidth * (1 + marginRatio) - barSize
 * Solving for marginRatio:
 *   marginRatio = (INTRA_GAP + BAR_HEIGHT - dodgeWidth) / dodgeWidth
 */
const _DODGE_BAR_WIDTH =
	(CATEGORY_COMPARISON_GROUP_HEIGHT_PX * (CATEGORY_COMPARISON_GROUP_HEIGHT_PX / (CATEGORY_COMPARISON_GROUP_HEIGHT_PX + CATEGORY_COMPARISON_INTER_CATEGORY_GAP_PX))) / 2;
export const CATEGORY_COMPARISON_DODGE_MARGIN_RATIO =
	(CATEGORY_COMPARISON_INTRA_GAP_PX + CATEGORY_BREAKDOWN_BAR_HEIGHT_PX - _DODGE_BAR_WIDTH) / _DODGE_BAR_WIDTH;

/**
 * Dodge only within the category band (not the inter-category gap). Matches 1 - paddingInner
 * so the 27px gap between Y-axis groups stays empty while current/previous stay 8px apart.
 */
export const CATEGORY_COMPARISON_DODGE_RATIO =
	CATEGORY_COMPARISON_GROUP_HEIGHT_PX / CATEGORY_COMPARISON_ROW_PITCH_PX;

/** Single-mode row pitch: 10px bar + 18px gap = 28px per category (compact, matches prod). */
export const CATEGORY_SINGLE_ROW_PITCH_PX =
	CATEGORY_SINGLE_BAR_HEIGHT_PX + CATEGORY_SINGLE_INTER_CATEGORY_GAP_PX;

/** Cat-scale paddingInner for single mode → 18px gap between bars when step is 28px. */
export const CATEGORY_SINGLE_CATEGORY_PADDING_INNER =
	CATEGORY_SINGLE_INTER_CATEGORY_GAP_PX / CATEGORY_SINGLE_ROW_PITCH_PX;

/**
 * Bottom canvas padding: tick labels + axis title offset + title line (~40 + 40 + 22).
 * Must be >= axis title offset in CategoryBreakdownBarChart or the title is clipped.
 */
export const CATEGORY_BREAKDOWN_CHART_PADDING_BOTTOM_PX = 72;

/**
 * Bottom canvas padding for single mode — adds extra room for the per-category legend
 * BizCharts places below the value axis (color swatches + names + pagination control).
 */
export const CATEGORY_SINGLE_CHART_PADDING_BOTTOM_PX = 110;

/**
 * Extra height reserved on top of the prod-pattern `data.length * 32` so the value axis,
 * its title, and the per-category legend sit below the bars without clipping.
 */
export const CATEGORY_SINGLE_LEGEND_RESERVE_PX = 130;

/** Chart canvas padding: top, right, bottom, left. */
export const CATEGORY_BREAKDOWN_CHART_PADDING_COMPARISON: [number, number, number, number] = [
	16,
	40,
	CATEGORY_BREAKDOWN_CHART_PADDING_BOTTOM_PX,
	200,
];
export const CATEGORY_BREAKDOWN_CHART_PADDING_SINGLE: [number, number, number, number] = [
	24,
	40,
	CATEGORY_SINGLE_CHART_PADDING_BOTTOM_PX,
	200,
];

export const resolveCategoryBreakdownChartPadding = (
	hasComparison: boolean,
	override?: [number, number, number, number]
): [number, number, number, number] => {
	const basePadding = hasComparison
		? CATEGORY_BREAKDOWN_CHART_PADDING_COMPARISON
		: override || CATEGORY_BREAKDOWN_CHART_PADDING_SINGLE;

	const minBottom = hasComparison
		? CATEGORY_BREAKDOWN_CHART_PADDING_BOTTOM_PX
		: CATEGORY_SINGLE_CHART_PADDING_BOTTOM_PX;

	if (basePadding[2] >= minBottom) {
		return basePadding;
	}

	return [basePadding[0], basePadding[1], minBottom, basePadding[3]];
};

export const computeCategoryBreakdownChartHeight = (
	categoryCount: number,
	hasComparison: boolean,
	chartPadding?: [number, number, number, number]
): number => {
	if (categoryCount <= 0) {
		return 200;
	}

	if (!hasComparison) {
		// Match the production BarChart sizing exactly: `data.length * 32`, then add the
		// extra vertical room our card reserves for the bottom legend so it doesn't
		// overlap the value axis or get clipped.
		return Math.max(
			200,
			categoryCount * CATEGORY_SINGLE_ROW_PITCH_BASE_PX + CATEGORY_SINGLE_LEGEND_RESERVE_PX
		);
	}

	const resolvedPadding = resolveCategoryBreakdownChartPadding(hasComparison, chartPadding);
	const paddingTop = resolvedPadding[0];
	const paddingBottom = resolvedPadding[2];
	const verticalChrome = paddingTop + paddingBottom;
	const plotHeight = categoryCount * CATEGORY_COMPARISON_ROW_PITCH_PX;
	return Math.max(200, plotHeight + verticalChrome);
};
